import { createSelector } from 'reselect';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import each from 'lodash/each';
import pick from 'lodash/pick';
import pickBy from 'lodash/pickBy';
import omit from 'lodash/omit';
import get from 'lodash/get';
import toLower from 'lodash/toLower';
import { ROW_TYPE } from '$app/components/vendor/WorkOperationEditor';
import { penceToPounds } from '$app/utils/util';
import { getLineItemQuantity, getLineItemPrice, isLabour } from '../util';
import { LINE_ITEMS_TABLE_DATA, LINE_ITEMS_PRICE_COLUMNS } from '../constants';
import {
  quoteState,
  proposedQuoteState,
  bookingGarageStatusSelector,
  workDetailsState,
  bookingQuoteSelector,
} from '../selectors';

export default function quoteWorkOperationsSelector() {
  return createSelector(quoteState, (state) => {
    if (!state) return [];
    return prepareTableData(
      state
        .get('lineItems')
        .filter((li) => ['mot', 'service', 'other'].includes(li.get('type')))
        .toJS()
    );
  });
}

/** *
 * Labour line items must appear first!
 * We should have one labour line item only.
 */

export const prepareTableData = (lineItems) =>
  lineItems.map(prepareTableLineItem).sort((a, b) => (a.order > b.order ? 1 : -1));

const prepareTableLineItem = (lineItem) => {
  const { subLineItems, ...li } = pickBy(
    lineItem,
    (val, key) => !isNil(val) && LINE_ITEMS_TABLE_DATA.includes(key)
  );
  const { operationId, workSummary, title, source } = li;
  Object.assign(li, {
    key: operationId,
    workOperation: {
      value: source,
      text: workSummary || title,
    },
  });

  each(pick(li, LINE_ITEMS_PRICE_COLUMNS), (v, k) => (li[k] = penceToPounds(v)));
  if (!isEmpty(subLineItems)) {
    li.children = prepareTableData(subLineItems);
    // .map((sli) => omit(sli, ['fixedCost', 'fixedCostWithVAT']));
  }

  if (li.parentId) {
    /**
     * We cannot guarantee that labour line items will have a title of "Labour".
     */
    const childType = isLabour(li) ? ROW_TYPE.labour : ROW_TYPE.part;
    Object.assign(li, {
      quantity: getLineItemQuantity(li),
      price: getLineItemPrice(li),
      type: childType,
      /**
       * Force the labour line item to the top, we shouldn't rely on the quote engine to order this for us
       * */
      order: childType === ROW_TYPE.labour ? 0 : li.order,
    });
  }
  return hideLineItemColumns(li);
};

const hideLineItemColumns = (lineItem) => {
  // mot hide columns
  if (lineItem.accountCode === '201') return omit(lineItem, ['partsQuantity', 'partsPrice']);
  // hide columns for parents with children
  if (!isEmpty(lineItem.children)) {
    return omit(lineItem, ['labourTime', 'labourCost', 'partsQuantity', 'partsPrice']);
  }
  return lineItem;
};

export function approvedQuoteSelector() {
  return createSelector(quoteState, (state) => {
    if (!state) return [];
    return prepareTableData(
      state
        .get('lineItems')
        .filter((li) => ['mot', 'service', 'other'].includes(li.get('type')))
        .toJS()
    ).map((li) => Object.assign(li, { urgency: get(li, 'urgency', 'other') }));
  });
}

export function approvedQuoteTotalsSelector() {
  return createSelector(quoteState, (state) => {
    if (!state) return [];
    return prepareTableData([
      {
        garageCost: state.get('garageTotalPriceWithoutVAT', 0),
        garageCostWithVat: state.get('garageTotalPriceWithVAT', 0),
        fixedCost: state.get('customerTotalPriceWithoutVAT', 0),
        fixedCostWithVAT: state.get('customerTotalPriceWithVAT', 0),
        createdAt: state.get('createdAt', ''),
        workSummary: 'Total',
      },
    ]).map((i) => ({ ...i, className: 'total-row', key: 'total-approved' }));
  });
}

const getWorkStatus = (operationId, approved, declined, workStatus) => {
  const isApproved = approved.includes(operationId);
  const isDeclined = declined.includes(operationId);
  if (isApproved) return 'approved';
  if (isDeclined) return 'declined';
  return workStatus || null;
};

export function proposedQuoteSelector() {
  return createSelector(
    proposedQuoteState,
    bookingGarageStatusSelector,
    workDetailsState,
    (quoteState, bookingGarageStatus, workDetails) => {
      if (!quoteState || !bookingGarageStatus) return [];
      const setProposedData = (li, workStatus) =>
        Object.assign(li, {
          className: classNames('additional-item', {
            'additional-item--declined': get(li, 'workStatus', workStatus) === 'declined',
          }),
          quoteType: 'proposed',
          bookingGarageStatus,
          quoteId: quoteState.get('quoteId'),
        });
      const approved = workDetails.get('approvedProposedWorkOperations', []);
      const declined = workDetails.get('declinedProposedWorkOperations', []);
      return prepareTableData(
        quoteState
          .get('lineItems')
          .filter((li) => ['mot', 'service', 'other'].includes(li.get('type')))
          .toJS()
          .map((li) => ({
            ...li,
            workStatus: getWorkStatus(li.operationId, approved, declined, li.workStatus),
          }))
      ).map((li) => {
        if (li.children) li.children = li.children.map((sli) => setProposedData(sli, li.workStatus));
        return setProposedData(li);
      });
    }
  );
}

export function proposedQuoteTotalsSelector() {
  return createSelector(proposedQuoteState, bookingQuoteSelector, (proposedQuote, bookingQuote) => {
    if (!proposedQuote || !bookingQuote) return [];
    return prepareTableData([
      {
        workSummary: 'Total with additional operations',
        garageCost:
          proposedQuote.get('garageTotalPriceWithoutVAT', 0) +
          get(bookingQuote, 'garageTotalPriceWithoutVAT', 0),
        garageCostWithVat:
          proposedQuote.get('garageTotalPriceWithVAT', 0) + get(bookingQuote, 'garageTotalPriceWithVAT', 0),
        fixedCost:
          proposedQuote.get('customerTotalPriceWithoutVAT', 0) +
          get(bookingQuote, 'customerTotalPriceWithoutVAT', 0),
        fixedCostWithVAT:
          proposedQuote.get('customerTotalPriceWithVAT', 0) +
          get(bookingQuote, 'customerTotalPriceWithVAT', 0),
      },
    ]).map((li) => Object.assign(li, { key: 'total-proposed' }));
  });
}

export function proposedQuoteApprovedWorkOperationSelector() {
  return createSelector(workDetailsState, (state) => state.get('approvedProposedWorkOperations', []));
}

export function proposedQuoteDeclinedWorkOperationSelector() {
  return createSelector(workDetailsState, (state) => state.get('declinedProposedWorkOperations'));
}

export const approvedProposedWorkOperationsSagaSelector = (state) =>
  state.getIn(['workDetails', 'approvedProposedWorkOperations'], []);

export const declinedProposedWorkOperationsSagaSelector = (state) =>
  state.getIn(['workDetails', 'declinedProposedWorkOperations'], []);

export function hasFinalisedProposedQuote() {
  return createSelector(workDetailsState, proposedQuoteState, (state, quoteState) => {
    if (!state || !quoteState) return false;
    const items = quoteState
      .get('lineItems')
      .filter(
        (li) =>
          ['mot', 'service', 'other'].includes(li.get('type')) &&
          !['approved', 'declined'].includes(li.get('workStatus'))
      )
      .map((li) => li.get('operationId'))
      .toJS();
    const newlyApproved = state.get('approvedProposedWorkOperations', []).toJS();
    const newlyDeclined = state.get('declinedProposedWorkOperations', []).toJS();
    const remaining = items.filter(
      (workOperationId) => ![...newlyApproved, ...newlyDeclined].includes(workOperationId)
    );
    return remaining.length === 0;
  });
}
